import { Form, message, Typography } from 'antd';
import { useSearchParamsForm, useServiceTypesOptions } from 'hooks';
import React, { useEffect, useRef, useState } from 'react'
import { FindWithKeywordDto, ServiceTypeHierarchyDto } from 'types';
import { ServiceTypeFromRefProps } from './ServiceTypeForm';
import { ColumnsType } from 'antd/es/table';
import { removeVietnameseAccents } from 'utils';
import { InputSearch, Table } from 'components/common';
import { serviceTypesMessages } from 'messages';
import { useGetServiceTypeDetailQuery } from 'services';

type FindType = FindWithKeywordDto;
type ServiceTypeListOptionProps = {
    onSelectServiceType: (serviceTypeId: number) => void;
    parentId: number;
}
type ServiceTypeFlatDto = {
  serviceTypeId: number;
  name: string;
  parentId?: number;
  children?: ServiceTypeFlatDto[];
  isRootParent?: boolean;
};
const ServiceTypeListOptions: React.FC<ServiceTypeListOptionProps> = ({onSelectServiceType, parentId}) => {
    const {setSearchParams, getSearchParams} = useSearchParamsForm();
    const [expandedKeys, setExpandedKeys] = useState<number[]>([]);

    const [form] = Form.useForm<FindType>();
    const values = Form.useWatch([], form) || getSearchParams();
    
    const {serviceTypesOptions, isLoading} = useServiceTypesOptions({
        parentId,
        minLevel: parentId === 147 ? 3 : 2,
    })
    
    const { data: serviceType, isLoading: isLoadingServiceTypeDetail } = useGetServiceTypeDetailQuery(parentId!, {
      skip: !parentId,
      refetchOnMountOrArgChange: true
    });

    const [open, setOpen] = useState(false);
    const [serviceTypeId, setServiceTypeId] = useState<number | undefined>();
    const expandFirstRef = useRef<boolean>(false);
    const serviceTypeFormRef = useRef<ServiceTypeFromRefProps>(null);
    useEffect(() => {
        if (!open && serviceTypeFormRef.current) {
            setServiceTypeId(undefined);
            serviceTypeFormRef.current.form.resetFields();
        }
    }, [open, serviceTypeFormRef.current]);
    const transformServiceTypesToTree = (data: any[], isRoot = true): ServiceTypeFlatDto[] => {
      return data.map((item) => ({
          serviceTypeId: item.serviceTypeId,
          name: item.name,
          parentId: item.parentId,
          children: item.childrenCompact ? transformServiceTypesToTree(item.childrenCompact) : undefined,
          isRootParent: isRoot && !!item.childrenCompact,
      }));
  };
  const flattenServiceTypes = (data: any[], parentId: number | null = null): ServiceTypeFlatDto[] => {
    let result: ServiceTypeFlatDto[] = [];
    data.forEach((item) => {
        result.push({
            serviceTypeId: item.serviceTypeId,
            name: item.name,
            parentId: parentId ?? undefined,
        });
        if (item.childrenCompact) {
            result = result.concat(flattenServiceTypes(item.childrenCompact, item.serviceTypeId));
        }
    });
    return result;
};
    // const getAllKeys = (data: ServiceTypeHierarchyDto[]): number[] => {
    //   let keys: number[] = [];
    //   if (!data) return [];
    //   data.forEach((item) => {
    //       keys.push(item.serviceTypeId);
    //       if (item.childrenCompact) {
    //           keys = keys.concat(getAllKeys(item.childrenCompact));
    //       }
    //   });
    //   return keys;
    // };
    // const transformedData = transformServiceTypes(serviceTypesOptions || []);
    const columns: ColumnsType<ServiceTypeHierarchyDto> = [
        {
            dataIndex: 'name',
            key: 'name',
            render: (val) => (
            <Typography.Paragraph ellipsis={{ rows: 2, tooltip: true }} className='mb-0 text-sm'>
                {val ?? '-'}
            </Typography.Paragraph>
            )
        },
    ];
    const handleOnExpand = (expanded: boolean, record: ServiceTypeHierarchyDto) => {
        if (expanded) {
            setExpandedKeys((prev) => [...prev, record.serviceTypeId]);
        } else {
            setExpandedKeys((prev) => prev.filter((key) => key !== record.serviceTypeId));
        }
    };
    const searchServiceTypes = (data: ServiceTypeFlatDto[], keyword = ''): ServiceTypeFlatDto[] => {
      const trimKeyword = removeVietnameseAccents(keyword).toLowerCase();
      const result: ServiceTypeFlatDto[] = [];
      data.forEach((serviceType) => {
          const isValid = removeVietnameseAccents(serviceType.name).toLowerCase().includes(trimKeyword);
          const children =
              serviceType.children && serviceType.children.length > 0
                  ? searchServiceTypes(serviceType.children, keyword)
                  : [];
          if (isValid || children.length > 0) {
              result.push({
                  ...serviceType,
                  children: children.length > 0 ? children : undefined,
              });
          }
      });
      return result;
  };
  const filteredData = searchServiceTypes(transformServiceTypesToTree(serviceTypesOptions || []), values.keyword);
    return (
        <div className='container-list rounded-t-none'>
          <Form
            scrollToFirstError={{ behavior: 'smooth', block: 'start' }}
            form={form}
            initialValues={getSearchParams()}
            onValuesChange={() => {
              const values = form.getFieldsValue();
              setSearchParams(values);
            }}
          >
            <div>
                <div className='mb-4 flex justify-center'>
                <Typography.Text className='text-center font-bold text-[20px]/[26px] text-[#141414] tracking-normal opacity-100'>{serviceType?.data.name}</Typography.Text>
                </div>
              <div className='flex items-center gap-4'>
                <Form.Item<FindType> className='mb-0 mr-6 max-w-[400px] flex-1' name='keyword'>
                  <InputSearch placeholder={serviceTypesMessages.name} />
                </Form.Item>
              </div>
            </div>
          </Form>
          <div>
          <Table
    className="rounded-none"
    showHeader={false}
    loading={isLoading}
    count={filteredData.length}
    columns={[
        {
            dataIndex: 'name',
            key: 'name',
            render: (val) => (
                <Typography.Paragraph ellipsis={{ rows: 2, tooltip: true }} className="mb-0 text-sm">
                    {val ?? '-'}
                </Typography.Paragraph>
            ),
        },
    ]}
    dataSource={filteredData}
    rowKey={(record: ServiceTypeFlatDto) => record.serviceTypeId}
    expandable={{
        expandedRowKeys: expandedKeys,
        onExpand: (expanded, record) => {
            if (expanded) {
                setExpandedKeys((prev) => [...prev, record.serviceTypeId]);
            } else {
                setExpandedKeys((prev) => prev.filter((key) => key !== record.serviceTypeId));
            }
        },
    }}
    onRow={(record) => ({
        onClick: () => {
            if (record.children && record.children.length > 0) {
                const isExpanded = expandedKeys.includes(record.serviceTypeId);
                if (isExpanded) {
                    setExpandedKeys((prev) => prev.filter((key) => key !== record.serviceTypeId));
                } else {
                    setExpandedKeys((prev) => [...prev, record.serviceTypeId]);
                }
                return;
            }
            if (record.isRootParent) {
                message.warning('Vui lòng chọn loại dịch con để tạo phiếu!');
                return;
            }
            setServiceTypeId(record.serviceTypeId);
            onSelectServiceType(record.serviceTypeId);
        },
    })}
    rowClassName={(record) =>
        record.serviceTypeId === serviceTypeId ? 'selected-row' : 'hover:cursor-pointer'
    }
    scroll={{
        x: 1300,
        y: 800,
    }}
/>
          </div>

        </div>
    )
}

export default ServiceTypeListOptions